/**
 * Library Index document — the pure half of Documents_On_Site Prompt 3.
 *
 * Builds the "Library Map" SiteDocument for a site: a single
 * markdown-rendered manifest grouping every uploaded doc by its
 * AI-classified per-report-type relevance. The map lives in
 * `site.documents` under a stable id (`library-index-${siteId}`) and is
 * tagged `library-index` so the UI can pin it and the prompt builder can
 * prepend its content as a structured directory.
 *
 * Everything here is a deterministic function of `site.documents` with no
 * store or Firebase dependency, so server code and the Cloud Run worker
 * bundles can import it. The store-bound refresh (`refreshLibraryIndex`)
 * stays in `services/knowledge/libraryIndex.ts`.
 */
import type { SiteDocument } from '../../types/siteRag';
import type { ReportTypeId } from '../../config/reportTypes';
import { REPORT_TYPE_REGISTRY } from '../../config/reportTypes';

/** Stable id prefix; the full id is `library-index-${siteId}`. */
const LIBRARY_INDEX_PREFIX = 'library-index-';
/** Tag the UI + prompt builder match on. */
export const LIBRARY_INDEX_TAG = 'library-index';
/** Threshold for classifying a doc as "relevant to" a given report type. */
export const RELEVANCE_THRESHOLD = 0.6;
/** Stamp on every regenerated map so a future schema change can target old payloads. */
export const LIBRARY_INDEX_VERSION = '2026.05.07-libindex-v1';

/** Returns true when the SiteDocument is the auto-generated library map. */
export function isLibraryIndexDoc(doc: SiteDocument): boolean {
  return doc.id.startsWith(LIBRARY_INDEX_PREFIX) || (doc.tags ?? []).includes(LIBRARY_INDEX_TAG);
}

/** Convenience finder — returns the existing library-index doc on a site, if any. */
export function findLibraryIndex(documents: SiteDocument[] | undefined): SiteDocument | undefined {
  return (documents ?? []).find(isLibraryIndexDoc);
}

/* ── Markdown rendering ───────────────────────────────────────────── */

interface RenderInput {
  siteId: string;
  siteName: string;
  documents: SiteDocument[];
  /** Defaults to new Date().toISOString(); accepts a fixed value for tests. */
  generatedAt?: string;
}

/**
 * Render the library map markdown. Pure function of inputs — exported
 * so tests can validate the structure without round-tripping through
 * the store. Excludes any prior library-index doc (to avoid recursion).
 *
 * Schema:
 *   - H1 site title + generation timestamp + total count
 *   - For each ACTIVE report type with >= 1 relevant doc: H2 section
 *     listing fileName + summary + score (sorted desc)
 *   - "Couldn't classify" bucket at the end for docs whose `relevance`
 *     field is missing OR every per-type score is below the threshold.
 */
export function renderLibraryMarkdown(input: RenderInput): string {
  const { siteId, siteName, generatedAt = new Date().toISOString() } = input;
  // Soft-deleted docs must not appear in the index: it is regenerated into
  // the SED and steers AI doc-selection, so listing a deleted doc keeps it
  // influencing evidence after the user removed it (Redhead, 2026-07-28 —
  // a rebuilt SED still carried both deleted TEST docs in these blocks).
  const docs = input.documents.filter(d => !isLibraryIndexDoc(d) && !d.deletedAt);

  const lines: string[] = [];
  lines.push(`# Library Map — ${siteName}`);
  lines.push('');
  lines.push(`_Generated ${generatedAt} · ${docs.length} document${docs.length === 1 ? '' : 's'} · siteId \`${siteId}\`_`);
  lines.push('');
  lines.push(
    `Auto-generated index of every document attached to this site, grouped by AI-classified per-report-type relevance ` +
    `(threshold ${RELEVANCE_THRESHOLD}). Documents that didn't classify cleanly fall into the "Couldn't classify" bucket — ` +
    `re-run the admin "Reclassify all documents" action after the model improves to refresh those.`,
  );
  lines.push('');

  if (docs.length === 0) {
    lines.push('## Empty library');
    lines.push('');
    lines.push('No documents have been uploaded for this site yet.');
    return lines.join('\n');
  }

  const reportTypes = Object.values(REPORT_TYPE_REGISTRY).map(r => r.id) as ReportTypeId[];

  // Bucket each doc into every report-type it crosses the threshold for.
  // A doc may appear under multiple types; that's intentional — the AI
  // sees it from every angle that matters.
  type Entry = { doc: SiteDocument; score: number };
  const buckets = new Map<ReportTypeId, Entry[]>();
  for (const id of reportTypes) buckets.set(id, []);
  const unclassified: SiteDocument[] = [];

  for (const doc of docs) {
    const rel = doc.relevance;
    if (!rel || Object.keys(rel).length === 0) {
      unclassified.push(doc);
      continue;
    }
    let placedAnywhere = false;
    for (const id of reportTypes) {
      const score = rel[id];
      if (typeof score === 'number' && score >= RELEVANCE_THRESHOLD) {
        buckets.get(id)!.push({ doc, score });
        placedAnywhere = true;
      }
    }
    if (!placedAnywhere) unclassified.push(doc);
  }

  for (const id of reportTypes) {
    const entries = buckets.get(id)!;
    if (entries.length === 0) continue;
    const meta = REPORT_TYPE_REGISTRY[id];
    entries.sort((a, b) => b.score - a.score);

    lines.push(`## ${meta.label} (${entries.length})`);
    lines.push('');
    for (const { doc, score } of entries) {
      lines.push(formatDocLine(doc, score));
    }
    lines.push('');
  }

  if (unclassified.length > 0) {
    lines.push(`## Couldn't classify (${unclassified.length})`);
    lines.push('');
    lines.push(
      `_These documents either weren't scored by the classifier yet (legacy uploads) or scored below ${RELEVANCE_THRESHOLD} on every active report type._`,
    );
    lines.push('');
    for (const doc of unclassified) {
      lines.push(formatDocLine(doc));
    }
    lines.push('');
  }

  return lines.join('\n').trimEnd() + '\n';
}

function formatDocLine(doc: SiteDocument, score?: number): string {
  const stars = doc.priority ? ' ★' : '';
  const tags = doc.tags?.length ? ` [${doc.tags.slice(0, 4).join(', ')}]` : '';
  const summary = doc.summary?.trim() || 'No summary';
  const scoreText = typeof score === 'number' ? ` _(score ${score.toFixed(2)})_` : '';
  // Single-line bullet so the markdown stays grep-friendly when read by
  // the prompt builder. Long summaries are clipped to keep the map small
  // — full summaries live on the doc itself.
  const clipped = summary.length > 220 ? `${summary.slice(0, 217)}…` : summary;
  return `- **${doc.fileName}**${stars}${tags} — ${clipped}${scoreText}`;
}

/* ── Build / refresh ──────────────────────────────────────────────── */

/**
 * Builds the library-index SiteDocument from the current contents of
 * `site.documents`. Pure — does not write anywhere. Use
 * `refreshLibraryIndex(siteId)` to persist the result.
 *
 * `id` is deterministic so multiple builds replace in-place when the
 * caller arrays them through `updateSiteDocuments`.
 */
export interface BuildLibraryIndexArgs {
  siteId: string;
  siteName: string;
  documents: SiteDocument[];
  generatedAt?: string;
}

export function buildLibraryIndex(args: BuildLibraryIndexArgs): SiteDocument {
  const generatedAt = args.generatedAt ?? new Date().toISOString();
  const markdown = renderLibraryMarkdown({
    siteId: args.siteId,
    siteName: args.siteName,
    documents: args.documents,
    generatedAt,
  });
  return {
    id: `${LIBRARY_INDEX_PREFIX}${args.siteId}`,
    fileName: 'Library Map (auto-generated).md',
    mimeType: 'text/markdown',
    sizeBytes: markdown.length,
    summary: `Library map of ${args.documents.filter(d => !isLibraryIndexDoc(d)).length} document(s) grouped by report-type relevance.`,
    tags: [LIBRARY_INDEX_TAG],
    textContent: markdown,
    uploadedAt: generatedAt,
    priority: false,
    includeInReport: false,
    relevanceModelVersion: LIBRARY_INDEX_VERSION,
  };
}

/**
 * Splices the library-index doc into the documents array, replacing
 * any existing instance. Returns a new array — does not mutate input.
 */
export function applyLibraryIndex(
  documents: SiteDocument[],
  index: SiteDocument,
): SiteDocument[] {
  const filtered = documents.filter(d => !isLibraryIndexDoc(d));
  // Pin the index at the front so DocumentLibrary can render it without
  // reshuffling — and so the prompt builder finds it on a sorted scan.
  return [index, ...filtered];
}
